import { MapPin, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useState } from "react";

export function TerritoryMap({ territories }) {
  const [hovered, setHovered] = useState(null);

  if (!territories || territories.length === 0) {
    return (
      <div data-testid="territory-map" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">
            <MapPin size={10} strokeWidth={1.5} style={{ display: "inline", marginRight: 6 }} />
            GEO-INTELIGENCIA
          </span>
          <span className="font-mono" style={{ fontSize: 9, color: "#52525b" }}>0 ZONAS</span>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span className="font-mono" style={{ fontSize: 9, color: "#52525b", textAlign: "center", lineHeight: 1.6 }}>
            Ejecuta GEO para detectar<br />micro-territorios con demanda al alza
          </span>
        </div>
      </div>
    );
  }

  // Normalize lat/lng
  const lats = territories.map(t => t.lat).filter(Boolean);
  const lngs = territories.map(t => t.lng).filter(Boolean);
  if (lats.length === 0) return null;

  const minLat = Math.min(...lats) - 2;
  const maxLat = Math.max(...lats) + 2;
  const minLng = Math.min(...lngs) - 2;
  const maxLng = Math.max(...lngs) + 2;

  const toX = (lng) => ((lng - minLng) / (maxLng - minLng)) * 90 + 5;
  const toY = (lat) => (1 - (lat - minLat) / (maxLat - minLat)) * 82 + 9;

  const getColor = (score) => {
    if (score >= 75) return "#34C759";
    if (score >= 50) return "#FFCC00";
    return "#FF9500";
  };

  const getSize = (score) => 4 + ((score || 50) / 100) * 8;

  return (
    <div data-testid="territory-map" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div className="panel-header">
        <span className="panel-title">
          <MapPin size={10} strokeWidth={1.5} style={{ display: "inline", marginRight: 6 }} />
          GEO-INTELIGENCIA
        </span>
        <span className="font-mono" style={{ fontSize: 9, color: "#71717a" }}>
          {territories.length} MICRO-ZONAS
        </span>
      </div>

      <div style={{
        flex: 1, position: "relative",
        background: "linear-gradient(180deg, rgba(0,122,255,0.02) 0%, transparent 100%)",
        border: "1px solid rgba(255,255,255,0.04)",
        overflow: "hidden", minHeight: 100
      }}>
        {/* Grid */}
        {[25, 50, 75].map(p => (
          <div key={`h${p}`} style={{ position: "absolute", left: 0, right: 0, top: `${p}%`, borderTop: "1px solid rgba(255,255,255,0.02)" }} />
        ))}
        {[25, 50, 75].map(p => (
          <div key={`v${p}`} style={{ position: "absolute", top: 0, bottom: 0, left: `${p}%`, borderLeft: "1px solid rgba(255,255,255,0.02)" }} />
        ))}

        {/* Dots */}
        {territories.map((t, i) => {
          const x = toX(t.lng);
          const y = toY(t.lat);
          const score = t.demand_index || t.demand_score || 50;
          const color = getColor(score);
          const size = getSize(score);

          return (
            <div key={t.id || i} data-testid={`territory-dot-${i}`}
              className="territory-dot"
              style={{
                left: `${x}%`, top: `${y}%`, width: size, height: size,
                background: color, color: color, boxShadow: `0 0 ${size * 2}px ${color}40`
              }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {hovered === i && (
                <div style={{
                  position: "absolute", bottom: size + 8, left: "50%", transform: "translateX(-50%)",
                  background: "#1c1c1f", border: "1px solid rgba(255,255,255,0.15)",
                  padding: "8px 10px", borderRadius: 2, whiteSpace: "nowrap", zIndex: 20, minWidth: 180
                }}>
                  <div className="font-mono" style={{ fontSize: 10, color: "#fff", fontWeight: 500, marginBottom: 3 }}>
                    {t.neighborhood}, {t.city}
                  </div>
                  <div className="font-mono" style={{ fontSize: 9, color: "#a1a1aa" }}>
                    {t.country} / {t.product_brand || t.language?.toUpperCase()}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <span className="font-mono" style={{ fontSize: 9, color }}>
                      IDX: {score}
                    </span>
                    <span className="font-mono" style={{ fontSize: 9, color: t.trend === "rising" ? "#34C759" : "#FFCC00" }}>
                      {t.trend === "rising" ? <TrendingUp size={9} style={{display: "inline"}} /> : <Minus size={9} style={{display: "inline"}} />}
                      {t.trend_velocity_pct_month ? ` +${t.trend_velocity_pct_month}%/mo` : ` ${t.trend}`}
                    </span>
                  </div>
                  {t.estimated_monthly_sales > 0 && (
                    <div className="font-mono" style={{ fontSize: 9, color: "#71717a", marginTop: 2 }}>
                      Est: {t.estimated_monthly_sales} ventas/mes
                    </div>
                  )}
                  {t.population_segment && (
                    <div className="font-mono" style={{ fontSize: 8, color: "#52525b", marginTop: 2 }}>
                      {t.population_segment}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
        {[
          { label: "ALTA >75", color: "#34C759" },
          { label: "MEDIA >50", color: "#FFCC00" },
          { label: "EMERGENTE", color: "#FF9500" },
        ].map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: l.color }} />
            <span className="font-mono" style={{ fontSize: 7, color: "#52525b" }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
